import {useEffect} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {useIntl} from 'react-intl';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {Box, Container, Flex, Heading, Text} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {setApiLocale} from '@eksab/api';
import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

export default function TermsAndConditions() {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  useEffect(() => {
    mixpanel?.track('Page View', {page: 'Terms and Conditions'});
  });

  return (
    <>
      <Head>
        <meta name="description" content="Predict game score and win prizes | Eksab" />
        <title>{`${t('terms-and-conditions')} | ${t('app.name')}`}</title>
      </Head>

      <Container py={py} flexDir="column" gap="10" maxW="75rem" mx="0" mb="10">
        <Heading as="h1" size="2xl" textTransform="uppercase">
          {t('terms-and-conditions')}
        </Heading>

        <Text lineHeight="1.8">{t('terms-and-conditions.description')}</Text>

        <Flex as="section" flexDir="column" gap="4" mb="5">
          {TITLES.map((title, index) => (
            <Box
              key={title}
              as="a"
              href={`#${title}`}
              _before={{content: `"${format(index + 1)}. "`, fontWeight: 500}}
              w="fit-content"
              fontSize="lg"
              fontWeight="700"
              color="gray.800"
              _hover={{color: 'green.400'}}
              transition="color 0.2s"
            >
              {t(`terms-and-conditions.${title}`)}
            </Box>
          ))}
        </Flex>

        <Flex as="section" flexDir="column" gap="16" lineHeight="2">
          {Object.entries(TERMS_AND_CONDITIONS).map(([termName, termValue], sectionIndex) => (
            <Flex key={termName} id={TITLES[sectionIndex]} flexDir="column" gap="6" scrollMarginTop="10">
              <Heading as="h2" size="lg" _before={{content: `"${format(sectionIndex + 1)}. "`, fontWeight: 600}}>
                {t(`${termName as keyof typeof TERMS_AND_CONDITIONS}`)}
              </Heading>

              <Flex as="ol" listStyleType="none" ps="4" flexDir="column" gap="4">
                {termValue.map((value, termIndex) =>
                  typeof value === 'number' ? (
                    <Text
                      key={`${termIndex}-${value}`}
                      as="li"
                      textAlign="justify"
                      fontSize={{base: 'md', lg: 'lg'}}
                      _before={{content: `"${format(sectionIndex + 1)}.${format(termIndex + 1)}. "`, fontWeight: 600}}
                    >
                      {t(`${termName as keyof typeof TERMS_AND_CONDITIONS}.term.${(termIndex + 1) as 1}`, {
                        br: () => <br />,
                      })}
                    </Text>
                  ) : (
                    <>
                      <Text
                        key={`${termIndex}-${value}`}
                        as="li"
                        textAlign="justify"
                        fontSize={{base: 'md', lg: 'lg'}}
                        _before={{content: `"${format(sectionIndex + 1)}.${format(termIndex + 1)}. "`, fontWeight: 600}}
                      >
                        {t(`${termName as keyof typeof TERMS_AND_CONDITIONS}.term.${(termIndex + 1) as 1}`)}
                      </Text>

                      <Flex as="ol" listStyleType="none" ms="5" flexDir="column" gap="2">
                        {Array.from({length: value.subs}).map((_, subIndex) => (
                          <Text
                            as="li"
                            key={`${termIndex}-${value}-${subIndex}`}
                            _before={{
                              content: `"${format(sectionIndex + 1)}.${format(termIndex + 1)}.${format(
                                subIndex + 1,
                              )}. "`,
                              fontWeight: 600,
                            }}
                            textAlign="justify"
                          >
                            {t(
                              // @ts-ignore
                              `${termName}.term.${termIndex + 1}.sub.${subIndex + 1}`,
                            )}
                          </Text>
                        ))}
                      </Flex>
                    </>
                  ),
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Container>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({locale}) => {
  setApiLocale(locale);

  const queryClient = new QueryClient();
  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};

const TITLES = [
  'the-promoter',
  'eligibility',
  'announcing-the-competition',
  'participation-in-the-competition',
  'competition-rules',
  'how-the-winner(s)-are-determined',
  'the-prize(s)',
  'unclaimed-prizes',
  'taxes',
  'use-of-personal-information-of-an-eligible-participant',
  'intellectual-property-and-moral-rights',
  'publicity',
  'general-conditions',
  'governing-law-and-jurisdiction',
] as const;

const TERMS_AND_CONDITIONS = {
  'terms-and-conditions.the-promoter': [1, 2, 3],
  'terms-and-conditions.eligibility': [1],
  'terms-and-conditions.announcing-the-competition': [{subs: 4}],
  'terms-and-conditions.participation-in-the-competition': [1],
  'terms-and-conditions.competition-rules': [1, 2, 3, 4, 5, 6, 7],
  'terms-and-conditions.how-the-winner(s)-are-determined': [1, 2, 3],
  'terms-and-conditions.the-prize(s)': [1, {subs: 3}, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
  'terms-and-conditions.unclaimed-prizes': [1, 2, 3, 4],
  'terms-and-conditions.taxes': [1, 2],
  'terms-and-conditions.use-of-personal-information-of-an-eligible-participant': [1, 2, 3, 4],
  'terms-and-conditions.intellectual-property-and-moral-rights': [1, 2, 3],
  'terms-and-conditions.publicity': [1],
  'terms-and-conditions.general-conditions': [1, 2, 3],
  'terms-and-conditions.governing-law-and-jurisdiction': [1],
};
